import { Suspense, useEffect } from "react";
import * as THREE from "three";
import { OrbitControls, useGLTF, Center, useTexture } from "@react-three/drei";
import { applyColor, applyTexture, setMeshVisibility } from "./materials.js";

const MESH_MAP = {
  handle: ["handle"],
  list: ["list"],
  skirting: ["skirting"],
  glassFrame: ["wall_unit_frame"],
  baseUnit: ["base_unit", "base_unit_door", "body_unit", "body_unit_door"],
  wallUnit: ["wall_unit", "wall_unit_door"],
  tallUnit: ["tall_unit", "tall_unit_door"],
  countertop: ["countertop"],
};

// Fixed standard colors — not user-selectable
const COLOR_MATTE_BLACK = { hex: "#1a1a1a", roughness: 1.0, metalness: 0.0 };
const COLOR_DARK_BRONZE = { hex: "#494541", roughness: 0.5, metalness: 0.3 };

const DEFAULT_WOOD = "/textures/kitchen_cabinets/Alpine White W1100 ST9.jpg";
const DEFAULT_CT = "/textures/countertop/QW.jpg";

function textureUrl(value, fallback) {
  return typeof value === "string" && value && !value.startsWith("data:")
    ? value
    : fallback;
}

function colorValue(value) {
  if (value && typeof value === "object" && typeof value.color === "string") {
    return value.color;
  }

  return "";
}

function applyMaterial(scene, meshNames, texture, selectedValue, overrides = {}) {
  const color = colorValue(selectedValue);

  if (color) {
    applyColor(scene, meshNames, {
      hex: color,
      roughness: overrides.roughness ?? 1.0,
      metalness: overrides.metalness ?? 0.0,
    });
    return;
  }

  applyTexture(scene, meshNames, texture, overrides);
}

function KitchenModel({ path, textures }) {
  const { scene } = useGLTF(path);
  const isGalley = path.toLowerCase().includes("galley");
  const rotation = isGalley ? [0, THREE.MathUtils.degToRad(60), 0] : [0, 0, 0];

  const loaded = useTexture({
    baseUnit: textureUrl(textures.baseUnit, DEFAULT_WOOD),
    wallUnit: textureUrl(textures.wallUnit, DEFAULT_WOOD),
    tallUnit: textureUrl(textures.tallUnit, DEFAULT_WOOD),
    countertop: textureUrl(textures.countertop, DEFAULT_CT),
  });

  useEffect(() => {
    applyColor(scene, MESH_MAP.list, COLOR_MATTE_BLACK);
    applyColor(scene, MESH_MAP.skirting, COLOR_MATTE_BLACK);
    applyColor(scene, MESH_MAP.glassFrame, COLOR_DARK_BRONZE);

    if (textures.handle === "hidden") {
      setMeshVisibility(scene, MESH_MAP.handle, false);
    } else {
      setMeshVisibility(scene, MESH_MAP.handle, true);
      applyColor(scene, MESH_MAP.handle, COLOR_MATTE_BLACK);
    }

    const woodMat = { roughness: 1.0, metalness: 0.0 };
    applyMaterial(scene, MESH_MAP.baseUnit, loaded.baseUnit, textures.baseUnit, woodMat);
    applyMaterial(scene, MESH_MAP.wallUnit, loaded.wallUnit, textures.wallUnit, woodMat);
    applyMaterial(scene, MESH_MAP.tallUnit, loaded.tallUnit, textures.tallUnit, woodMat);
    applyMaterial(scene, MESH_MAP.countertop, loaded.countertop, textures.countertop, {
      roughness: 1.0,
      metalness: 0.0,
    });
  }, [
    scene,
    loaded,
    textures.handle,
    textures.baseUnit,
    textures.wallUnit,
    textures.tallUnit,
    textures.countertop,
  ]);

  return (
    <Center>
      <primitive object={scene} rotation={rotation} />
    </Center>
  );
}

export default function Experience({ modelPath, textures }) {
  return (
    <>
      <color attach="background" args={["#f7f7f7"]} />
      <OrbitControls makeDefault />
      <hemisphereLight args={["#ffffff", "#cccccc", 2.5]} />
      <directionalLight position={[0, 6, 5]} intensity={0.6} />
      <directionalLight position={[-5, 4, -3]} intensity={0.3} />
      <directionalLight position={[5, 4, -3]} intensity={0.3} />

      {modelPath && (
        <Suspense fallback={null}>
          <KitchenModel path={modelPath} textures={textures} />
        </Suspense>
      )}
    </>
  );
}
